import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { TreeSelect as AntdTreeSelect, TreeSelectProps } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import type { BaseSelectRef as RcBaseSelectRef } from 'rc-select';
import type { CheckedStrategy } from 'rc-tree-select/lib/utils/strategyUtil';
import type { CustomTagProps as RcCustomTagProps } from 'rc-select/lib/BaseSelect';
import { isEqual } from 'lodash';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import SelectAllSwitch, { DEFAULT_SELECT_ALL_THRESHOLD } from '../common/select-all-switch/select-all-switch';
import InfoMessage from './components/info-message/info-message';

import './tree-select.scss';
import DummyCheckbox from './components/dummy-checkbox/dummy-checkbox';

// over this threshold the tree-select is having performance issue when the dropdown is closing
// basically it takes all the values and render a hidden span element for screen reader
const SELECTED_COUNT_PERFORMANCE_THRESHOLD = 1500;

const { SHOW_PARENT, SHOW_CHILD, SHOW_ALL } = AntdTreeSelect;

type RawValue = string | number;

interface Props extends Omit<TreeSelectProps, 'onChange'> {
	onChange?: (value: RawValue | RawValue[] | undefined) => void;
	allowSelectAll?: boolean;
	selectAllText?: string;
	selectAllTagText?: string;
	selectAllThreshold?: number;
	emptyAsAll?: boolean;
	infoMessage?: string;
	showSelectAllCounter?: boolean;
}

export type BaseSelectRef = RcBaseSelectRef;
export type TreeSelectCustomTagProps = RcCustomTagProps;
export type TreeSelectOptionType = DefaultOptionType;

export const calculateValues = (
	treeData: DefaultOptionType[] | Omit<DefaultOptionType, 'children'>[],
	checkedStrategy: CheckedStrategy
) =>
	treeData
		.map((data: DefaultOptionType | Omit<DefaultOptionType, 'children'>) => {
			if (checkedStrategy === SHOW_ALL) {
				return data.children?.length
					? [data.value, ...calculateValues(data.children, checkedStrategy)]
					: data.value;
			}

			if (checkedStrategy === SHOW_CHILD) {
				return data.children?.length ? calculateValues(data.children, checkedStrategy) : data.value;
			}

			// SHOW_PARENT
			return data.value;
		})
		.flat();

const getInitValue = (initialValue: RawValue | RawValue[] | undefined, isMultiple: boolean) => {
	if (isMultiple) {
		if (initialValue) {
			initialValue = Array.isArray(initialValue) ? initialValue : [initialValue];
		}
	}

	return initialValue;
};

const isEmptyArray = (arr: any) => Array.isArray(arr) && !arr.length;

const InternalTreeSelect = forwardRef<BaseSelectRef, Props>(
	(
		{
			selectAllText,
			allowSelectAll,
			selectAllThreshold = DEFAULT_SELECT_ALL_THRESHOLD,
			emptyAsAll,
			onChange,
			className,
			dropdownClassName,
			popupClassName,
			defaultValue,
			value,
			multiple,
			treeCheckable,
			showCheckedStrategy = SHOW_PARENT,
			treeNodeFilterProp = 'label',
			maxTagCount = 'responsive',
			maxTagPlaceholder,
			selectAllTagText,
			infoMessage,
			showSelectAllCounter,
			...otherProps
		},
		treeRef
	) => {
		const initialValue = getInitValue(value || defaultValue, multiple);
		const indexedDisabledValues = useMemo<Record<RawValue, boolean>>(() => {
			if (!otherProps.treeData) {
				return {};
			}

			const disabledValues = {};
			const traverse = (data: DefaultOptionType[] | Omit<DefaultOptionType, 'children'>[]) => {
				data.forEach(item => {
					disabledValues[item.value] = Boolean(item.disabled);

					if (item.children?.length) {
						traverse(item.children);
					}
				});
			};

			traverse(otherProps.treeData);

			return disabledValues;
		}, [otherProps.treeData]);

		const allValues: RawValue[] = useMemo(
			() => (otherProps.treeData ? calculateValues(otherProps.treeData, showCheckedStrategy).sort() : []),
			[otherProps.treeData, showCheckedStrategy]
		);
		const noChildrenData = useMemo(() => !(otherProps.treeData || []).some(item => item.children?.length > 0), [
			otherProps.treeData
		]);

		const [selected, setSelected] = useState<RawValue | RawValue[]>();
		const [allSelected, setAllSelected] = useState<boolean>();

		useEffect(() => {
			setSelected(emptyAsAll && isEmptyArray(initialValue) ? allValues : initialValue);
			setAllSelected(
				allowSelectAll && ((emptyAsAll && isEmptyArray(initialValue)) || isEqual(initialValue, allValues))
			);
		}, [value, allValues, emptyAsAll, allowSelectAll]);

		const handleSelectAll = (checked: boolean) => {
			const values = checked ? allValues.filter(value => !indexedDisabledValues[value]) : [];
			let onChangeValue = values;

			if (emptyAsAll) {
				onChangeValue = checked ? [] : undefined;
			}

			onChange?.(onChangeValue);
			setSelected(values);
			allowSelectAll && setAllSelected(checked);
		};

		const handleOnchange = (value: RawValue | RawValue[]) => {
			let isAllSelected = false;
			let onChangeValue = value;

			if (allowSelectAll) {
				isAllSelected = isEqual((value as RawValue[]).sort(), allValues);

				if (emptyAsAll) {
					if (isAllSelected) {
						onChangeValue = [];
					} else if (isEmptyArray(value)) {
						onChangeValue = undefined;
					}
				}
			}

			onChange?.(onChangeValue);
			setSelected(value);
			allowSelectAll && setAllSelected(isAllSelected);
		};
		return (
			<AntdTreeSelect
				ref={treeRef}
				className={classNames('bringg-tree-select', className, {
					'big-data-tree': Array.isArray(selected) && selected.length > SELECTED_COUNT_PERFORMANCE_THRESHOLD
				})}
				switcherIcon={
					<BringgIcon iconName={BringgFontIcons.ChevronRight} data-test-id="select-tree-switcher" />
				}
				popupClassName={classNames('bringg-tree-dd', dropdownClassName, popupClassName, {
					'no-children-data': noChildrenData
				})}
				dropdownRender={menu => (
					<>
						{infoMessage && (
							<>
								<InfoMessage message={infoMessage} />
								<div className="select-all-divider" />
							</>
						)}
						{allowSelectAll && otherProps.treeData?.length > selectAllThreshold && (
							<>
								<SelectAllSwitch
									selectAllText={selectAllText}
									onSelectAll={handleSelectAll}
									checked={allSelected}
									indeterminate={!allSelected && Array.isArray(selected) && selected.length > 0}
								/>
								<div className="select-all-divider" />
							</>
						)}
						{menu}
					</>
				)}
				onChange={handleOnchange}
				value={selected}
				treeCheckable={allowSelectAll || multiple || treeCheckable}
				showCheckedStrategy={showCheckedStrategy}
				treeNodeFilterProp={treeNodeFilterProp}
				maxTagCount={allSelected && selectAllTagText ? 0 : maxTagCount}
				maxTagPlaceholder={allSelected && selectAllTagText ? selectAllTagText : maxTagPlaceholder}
				{...otherProps}
			/>
		);
	}
);

type CompoundedComponent = typeof InternalTreeSelect & {
	DummyCheckbox: typeof DummyCheckbox;
};

const TreeSelect = InternalTreeSelect as CompoundedComponent;
TreeSelect.DummyCheckbox = DummyCheckbox;

export default TreeSelect;
